import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RecipientSelect from '../components/RecipientSelect';
import FilePreviewModal from './FilePreviewModal';
import { getActiveSchemeClass } from '../utils/themeUtils';
import useBackdropClose from '../utils/useBackdropClose';

const MailFileModal = ({file, contacts, close, renameFile}) => {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  // Selection as it was when the picker opened (restored on cancel); null while the picker is closed.
  const [recipientsBeforePicker, setRecipientsBeforePicker] = useState(null);
  const [tempFileName, setTempFileName] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [note, setNote] = useState('');

  const backdrop = useBackdropClose(close);

  const pickerOpen = recipientsBeforePicker !== null;

  // Captured so Escape closes this modal without also closing the Files card
  // behind it. The preview handles its own Escape while it is open.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || previewDocument) return;
      e.stopPropagation();
      if (pickerOpen) {
        setSelectedRecipients(recipientsBeforePicker);
        setRecipientsBeforePicker(null);
      } else {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [close, previewDocument, pickerOpen, recipientsBeforePicker]);

  const selectRecipient = (recipient) => {
    setSelectedRecipients((current) => (
      current.includes(recipient) ? current.filter(r => r !== recipient) : [...current, recipient]
    ));
  }

  const mailFile = () => {
    console.log('mailFile', selectedRecipients, note);
  }

  const validateCharacterInput = (str) => {
    let reg = /^[a-zA-Z0-9\s]+$/;
    return reg.test(str);
  }

  const fileName = (fileName) => {
    const split = fileName.split('.');
    if (split.length === 1) return [split[0], ''];
    const extension = '.' + split.pop();
    return [split.join('.'), extension];
  }

  const saveRename = () => {
    if (!fileName(tempFileName)[0].trim()) return;
    setRenaming(true);
    renameFile(tempFileName, () => {
      setTempFileName(null);
      setRenaming(false);
    });
  }

  const cancelRename = () => {
    setTempFileName(null);
    setRenaming(false);
  }

  const fileSize = `${Math.floor((file.file_size / 1024 / 1024) * 100) / 100} MB`;

  return createPortal(
    <div className={`mail-file-layer ${getActiveSchemeClass()}`.trim()}>
      <div className="mail-file-backdrop" {...backdrop} role="presentation">
        <div className="mail-file-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Email file">
          <header className="mail-file-header">
            <div className="mail-file-heading">
              <h2 className="mail-file-title">Email file</h2>
              <p className="mail-file-meta">
                {selectedRecipients.length === 0 ? 'No recipients selected' : `${selectedRecipients.length} selected`}
              </p>
            </div>
            <button type="button" className="mail-file-close" onClick={() => close()} aria-label="Close">
              <CloseIcon />
            </button>
          </header>

          <div className="mail-file-body">
            <section className="mail-file-section">
              <span className="mail-file-label">File</span>
              <div className="mail-file-card">
                {tempFileName !== null ? (
                  <div className="mail-file-rename">
                    <input
                      type="text"
                      className="browser-default mail-file-rename-input"
                      aria-label="File name"
                      autoFocus
                      value={fileName(tempFileName)[0]}
                      onChange={(e) => {
                        if (e.target.value === '' || validateCharacterInput(e.target.value)) {
                          setTempFileName(e.target.value + fileName(tempFileName)[1])
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename();
                      }}
                    />
                    <span className="mail-file-rename-extension">{fileName(tempFileName)[1]}</span>
                    <button type="button" className="mail-file-icon-btn" onClick={saveRename} disabled={renaming || !fileName(tempFileName)[0].trim()} aria-label="Save file name">
                      <CheckIcon />
                    </button>
                    <button type="button" className="mail-file-icon-btn" onClick={cancelRename} aria-label="Cancel rename">
                      <CloseIcon />
                    </button>
                  </div>
                ) : (
                  <div className="mail-file-name-row">
                    <span className="mail-file-name">{file.file_name}</span>
                    <button type="button" className="mail-file-icon-btn" onClick={() => setTempFileName(file.file_name)} aria-label="Rename file" title="Rename">
                      <EditIcon />
                    </button>
                    <button type="button" className="mail-file-icon-btn" onClick={() => setPreviewDocument(true)} aria-label="Preview file" title="Preview">
                      <VisibilityIcon />
                    </button>
                  </div>
                )}
                <span className="mail-file-details">
                  Uploaded {file.uploaded_formatted} by {file.uploaded_by} · {fileSize}
                </span>
              </div>
            </section>

            <section className="mail-file-section">
              <span className="mail-file-label">Recipients</span>
              {selectedRecipients.length > 0 && (
                <ul className="mail-file-recipients">
                  {selectedRecipients.map(recipient => (
                    <li key={recipient} className="mail-file-recipient">
                      <span className="mail-file-recipient-email">{recipient}</span>
                      <button type="button" className="mail-file-recipient-remove" onClick={() => selectRecipient(recipient)} aria-label={`Remove ${recipient}`}>
                        <CloseIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" className="mail-file-pick" onClick={() => setRecipientsBeforePicker(selectedRecipients)}>
                <GroupAddIcon />
                {selectedRecipients.length > 0 ? 'Change recipients' : 'Select recipients'}
              </button>
            </section>

            <section className="mail-file-section">
              <label className="mail-file-label" htmlFor="mail-file-note">Note <span className="mail-file-optional">optional</span></label>
              <textarea
                id="mail-file-note"
                className="browser-default mail-file-note"
                placeholder="Add a note to the email"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </section>
          </div>

          <footer className="mail-file-footer">
            <button type="button" className="mail-file-cancel" onClick={() => close()}>Cancel</button>
            <button
              type="button"
              className="mail-file-send"
              onClick={() => mailFile(selectedRecipients, note)}
              disabled={selectedRecipients.length === 0}
            >
              <SendIcon />
              {selectedRecipients.length > 0 ? 'Send email' : 'Select recipients to send'}
            </button>
          </footer>
        </div>
      </div>

      {pickerOpen && (
        <RecipientSelect
          title="File Recipients"
          contacts={(contacts || []).filter(c => c.email)}
          selectedRecipients={selectedRecipients}
          handleRecipientSelect={selectRecipient}
          addNewContact={(email) => setSelectedRecipients((current) => (current.includes(email) ? current : [...current, email]))}
          submit={() => setRecipientsBeforePicker(null)}
          close={() => {
            setSelectedRecipients(recipientsBeforePicker);
            setRecipientsBeforePicker(null);
          }}
        />
      )}

      {previewDocument && (
        <FilePreviewModal file={file} close={() => setPreviewDocument(false)} />
      )}
    </div>,
    document.body
  );
};

export default MailFileModal;
